import { RichTooltipConfig } from '../rich-tooltip.interface';

export enum GridName {
  Item = 'Item',
  Vendor = 'Vendor',
  Supply = 'Supply',
  Demand = 'Demand',
  Bom = 'Bom',
  Supplier = 'Supplier',
  ManageSupplier = 'Address Book',
  Shipment = 'Shipment',
  ShipmentAWD = 'ShipmentAWD',
  SupplierList = 'Supplier List',
  AllAvailableItems = 'All Available Items',
  ForecastShipmentItem = 'Forecast Shipment Item',
  AmazonShipmentItem  = 'Amazon Shipment Item',
  ForecastShipmentItemChosen = 'Forecast Shipment Item Chosen',
  ForecastShipmentItemValid = 'Forecast Shipment Item Valid',
  SuggestedPos ='Suggested POs',
  ItemsInPo = 'Items in PO',
  ItemStickers = 'Item Stickers',
  RestockSuggestion = 'Restock Suggestion',
  ForecastedHistorySetting = 'Forecasted History Setting',
  ItemSite = 'Item Site',
  PurchaseOrder = 'PurchaseOrder',
  PurchaseOrderItem = 'PurchaseOrderItem',
  Campaigns = 'Campaigns',
  CampaignItems = 'CampaignItems',
  CampaignsByItem = 'CampaignsByItem',
  CampaignProductAdGroupedBySKU = 'CampaignProductAdGroupedBySKU',
  HistoryLogs = 'HistoryLogs',
  ItemPricingRecommendation = 'ItemPricingRecommendation'
}

/*
The logic of this tooltip is as follows:
+ Prioritize the specific item name:
  ASC: {
    item: {
      sku: 'The name used to identify products in the inventory',
    },
  },
If it doesn't exist then use the generic name:
generic: {
  sku: '(A global one) The name used to identify products in the inventory',
}

The logic of this rich tooltip is as follows:
+ if declaring the field name in
const richTooltipFieldMap: Record<string, Record<string, string>> = {
generic: {
    daysLeft: 'daysLeft', <--- Declare here for generic field to use rich tooltip
  },
  ASC: {
    [`dueDate${GridName.ItemsInPo}`]: 'daysLeft', <--- Declare here for specific field to use rich tooltip
  }
then it will navigate to "daysLeft" in the RICH_HEADER_TOOLTIP_CONFIGS object
export const RICH_HEADER_TOOLTIP_CONFIGS: Record<string, RichTooltipConfig> = {
  daysLeft: {
    header: 'daysLeft',
    formula: 'daysLeft = daysLeft',
    ...
  },

*/

const tooltips: Record<string, Record<string, any>> = {
  generic: {
    warehouseQtySupply: 'The units of the item are stored in your warehouse',
    reconciledQty: 'Units that have been received',
    matches:
      'Amazon has received a shipment with items contained on the purchase order',
    dueDate: 'Estimated date of receipt',
    openQty: 'Outstanding units on a sales order or invoice',
    orderQty: 'Total units on a sales order or invoice',
    poNum: 'Purchase order reference identifier',
    vendorName: 'Common business name of your supplier',
    availableQty: 'Amount recognized as unreconciled on the shipment',
    itemName: 'The name used to identify products in the inventory',
    poAvailability: 'Outstanding units on a purchase order',
    quantity: 'Amount recognized as received in the shipment',
    receivedDate: 'Date that Amazon received your items',
    availablewarehouseqty:
      'Inventory outside of Amazon related to items on the shipment',
    lineItems: 'Unique items contained on the shipment',
    shipmentId: 'ID Amazon assigned to your inbound shipment',
    s30d: 'Historic sales over period of time',
    s90d: 'Historic sales over period of time',
    s365d: 'Historic sales over period of time',
    s180d: 'Historic sales over period of time',
    s7d: 'Historic sales over period of time',
    daysRemaining: 'Days left to place your order to stay in stock',
    lotMultipleQty: 'Multiple an item must be ordered by from your supplier',
    moq: 'Minimum quantity your supplier requires you to purchase at a time',
    sMtd: 'Historic sales over period of time',
    onNewPo: 'Quantity of an item on a new PO since your last forecast',
    openPurchaseOrders:
      'Quantity of an item on existing POs prior to the last forecast',
    daysLeft: 'Days your current inventory is expected to last',
    outOfStockDate: 'Date your current inventory is expected to run out',
    meanLtd: 'Calculated prediction of future sales',
    purchaseQty: 'Adjustable quantity you want to order today',
    truePurchaseQty:'Recommended purchase quantity adjusted for daily sales rate, MOQ, and case pack quantity',
    // recommendedQty: 'Recommended order qty based on forecast models only',
    recommendedOrderDate: 'Latest order by date to stay in stock',
    safetyStockLtd: 'Cushion added for highly variable or important items',
    total: 'Total cost commitment for all units',
    vendorPrice: 'Adjustable cost per unit of a given item',
    sYtd: 'Historic sales over period of time',
    earliestRecommendedOrderDate: 'Latest order by date to stay in stock',
    totalPrice: 'Total cost commitment for all units',
    totalQty: 'Total units on the suggested PO',
    uniqueItems: 'Total distinct items included on the suggested PO',
    createdAt: 'The date the purchase order was created',
    documentTotal: 'Cost of all of the items on the PO',
    poRefNum: 'Purchase order reference identifier',
    qtyOrdered: 'Number of units contained with the purchase order',
    qtyReceived: 'Number of units to date that have been received',
    view: 'View or modify existing POs',
    asin: 'Amazon Standard identification number',
    refNum: 'Order reference number',
    description: 'Textual, referential description of your Item',
    isHidden: 'Hides from your forecasting and purchase recommendations',
    doNotOrder: 'Hides from your purchasing recommendations',
    doNotRestock: 'Hides from your restock recommendations',
    name: 'The unique identifier used to reference this item',
    leadTime:
      'Days it takes to become saleable from the time you place the order',
    orderInterval:
      'The preferred number of days between placing purchase orders',
    lotMultipleItemName:
      'You purchase an item as a different SKU than you sell it as',
    serviceLevel: 'Select safety stock level ranging from 0-10 with 0 being no safety stock and 10 being the most safety stock',
    tag: 'User defined categorization field',
    useBackfill: 'Toggles back-filled data in order to simulate seasonality',
    useHistoryOverride:
      'Toggles history overrides in order to improve accuracy',
    email: 'Supplier email address',
    onHand: 'Inventory you have in storage or at a place of sale',
    onHandMin: 'Min inventory in storage or at a place of sale',
    warehouseQty: 'Inventory you have outside of Amazon FBA and FBM',
    warehouseQtyMin: 'Min inventory you have outside of Amazon',
    onHandThirdPartyMin:
      'Min inventory to keep in stock outside an Amazon facility',
    parentName: 'The SKU (Item Name) of the Kit',
    childName: 'The SKU (Item Name) of a component item in the kit',
    qty: 'The amount of the component item included in one kit',
    docDate: 'The day the purchase order was created',
    history: 'History',
    earliestDueDate: 'Estimated date the purchase order will be received if the order is placed today.',
    snapshotQty: 'Demand - Purchase Orders',
    lastOrderDate: 'Last Order Date',
    nextScheduledOrderDate: 'Next Scheduled Order Date',
    daysUntilNextScheduledOrder: 'Days Until Next Scheduled Order',
    openSalesOrders: 'Open Sales Orders',
    createdAtItem: 'The date the item was created',
    isHiddenDemand: 'Removes demand from sales history',
    lostSale2d: 'Estimated number of units that could have been sold during stockout periods',
    lostSale7d: 'Estimated number of units that could have been sold during stockout periods',
    lostSale14d: 'Estimated number of units that could have been sold during stockout periods',
    lostSale30d: 'Estimated number of units that could have been sold during stockout periods',
    lostSale60d: 'Estimated number of units that could have been sold during stockout periods',
    lostSale90d: 'Estimated number of units that could have been sold during stockout periods',
    lostSale180d: 'Estimated number of units that could have been sold during stockout periods',
    lostSale365d: 'Estimated number of units that could have been sold during stockout periods',
    imageUrl: 'Item image',
    avgDailySpend90d: 'Average daily spend from the last 90 days of Spend, Orders, and Sales data',
    businessPrice: 'The current selling price of the item on Amazon',
    fnsku: 'FBA inventory ID',
    minPrice: 'The lowest price of the item on Amazon',
    maxPrice: 'The highest price of the item on Amazon',
    logCount: 'Tracks all historical updates for the campaign, including previous values, new values, change time, and the person who made the change',
    logCountItemPricingRecommendation: 'Tracks all historical updates for the item price, including previous values, new values, inventory status, change time, and the user who made the change',
    nameCampaigns: 'The name of the Amazon advertising campaign',
    startDateCampaigns: 'The date your campaign begins delivering ads',
    endDateCampaigns: 'The date your campaign stop delivering ads',
    avgDaysUntilStockOutCampaigns: 'Average value calculated from inventory and sales velocity for all items in a campaign',
    skuCountCampaigns: 'The total number of SKUs associated with a campaign',
    suggestBudgetCampaigns:'The suggest budget based on current inventory and the configured threshold settings',
    budgetCampaigns: 'The current daily budget of the campaign on Amazon Advertising',
    inventoryStatus: 'Inventory risk based on Average Days Until Stockout thresholds',
    itemHistoryLength: 'Number of historical days used to calculate average sales for the items',
    tags: 'User defined categorization field',
    removeAll: 'Click to remove all items',
    addAll: 'Click to add all items',
    // --- Keys added from ASC sections so other companies (QBFS, QBO, CSV, etc.) inherit them ---
    sku: 'The name used to identify products in the inventory',
    onHandThirdParty: 'The units of the item are stored in your warehouse',
    onHandFbm: 'Available FBM inventory synced from AMZ',
    awdInventoryQty: 'The total available inventory stored in AWD',
    inboundAwd: 'The number of units currently in transit to AWD synced from AMZ',
    isFbm: 'Indicates whether the item is merchant fulfilled',
    inventorySourcePreference: 'Inventory source used for forecasting calculations',
    packageWeight: 'Weight of the item used for calculating shipping costs',
    skuNotes: 'Internal notes related to the SKU',
    prepGuide: 'Amazon-required preparation instructions for the item',
    prepNotes: 'Internal notes related to item preparation',
    supplierRebate: 'Per-unit supplier discount used in margin calculations',
    inboundShippingCost: 'Cost to ship this item from the supplier to AMZ',
    reshippingCost: 'Cost to ship this item from your warehouse to AMZ',
    repackagingMaterialCost: 'Cost of materials used to repackage this item',
    repackingLaborCost: 'Cost of labor used to repackage item',
    dimensionalWeight: 'Dimensional weight calculated by AMZ',
    hazmat: 'Indicates whether the item requires special handling',
    oversized: 'Indicates whether the item is classified as oversized by Amazon',
    category: 'The category this item is classified as',
    upc: 'Universal Product Code for this item',
    ean: 'European Article Number for this item',
    rank: 'Amazon sales rank within its product category',
    average7DayPrice: 'Average Amazon selling price over the last 7 days',
    boxLength: 'Length of the box used to ship this item to Amazon',
    boxWidth: 'Width of the box used to ship this item to Amazon',
    boxHeight: 'Height of the box used to ship this item to Amazon',
    boxWeight: 'Weight of the box used to ship this item to Amazon',
    isCustom: 'Indicates whether the item is a custom product',
    totalSurcharge: 'Estimated storage surcharge for inventory over 180 days',
    links: 'Quick links to view this product on Amazon',
    localQty: 'Inventory you have outside of Amazon FBA and FBM',
    inbound: 'The number of units currently in transit synced from AMZ',
    inboundPrice: 'Your current selling price on Amazon',
    inboundSalesLast30Days: 'Amazon Sales Last 30 Days',
    inboundAvailable: 'Inbound Available',
    inboundFcTransfer: 'Total units currently being transferred between fulfillment centers',
    inboundFcProcessing: 'Units currently in Amazon reserved status for transfer or additional processing at a fulfillment center',
    inboundCustomerOrder: 'Units reserved for confirmed customer orders and are temporarily unavailable for other purchases',
    inboundUnfulfillable: 'Units synced from Amazon that are marked as unfulfillable',
    inboundAlert: 'Amazon Inventory Alert - Status: Out Of Stock, Low Stock',
    inboundWorking: 'Inbound inventory with working status',
    mwsFulfillmentFee: 'Fulfillment Fee',
    caseQty: 'Units in a single case pack',
    receiveDate: 'Update to use Expedite Recommendation',
    suggestPrice: 'Recommends an optimal listing price for the SKU on Amazon based on current inventory health',
    details: 'Quick links to view this product on Amazon',
    // Shipment & Purchase Order fields
    shipmentName: 'The name given to this shipment for easy reference',
    status: 'The current stage of the shipment',
    shipmentSource: 'Where the shipment is sent from',
    destinationFulfillmentCenterId: 'Where the shipment is being delivered to',
    options: 'Actions you can take for this shipment (view, edit, or manage)',
    updatedAt: 'The most recent date when the shipment was changed',
    nextStep: 'What you should do next for this shipment',
    restockType: 'The type of restocking used for this shipment',
    requestedQty: 'The number of items requested to be shipped',
    totalCost: 'The total cost of this shipment',
    orderNotes: 'Extra notes or comments about this shipment',
    receivedQty: 'The total quantity actually received from the shipment',
    shipTo: 'The location where the order will be delivered',
    // Supply fields
    reconciledStatus: 'The current stage of the shipment',
    orderKey: 'Unique Amazon Order ID for tracking and reference',
    itemKey: 'Combined ASIN and SKU used as the item identifier',
    // Vendor fields
    averageHistoryLength: 'Average monthly sales calculated across all items from this supplier',
    targetOrderValue: 'Target purchase order value ({currency}) for this supplier',
    freeFreightMinimum: 'Order value needed for free shipping',
    restockModel: 'Select how inventory is sent to Amazon FBA for this supplier',
    // Supplier List fields
    orangeAlerts: 'Count of items that need immediate restocking',
    greenAlerts: 'Count of items with healthy stock levels',
    yellowAlerts: 'Count of items that should be restocked soon',
    redAlerts: 'Count of items with no stock available',
    tealAlerts: 'Count of new items with no sales history yet',
    restockUnits: 'Total units recommended for restocking across suppliers',
    restockTotal: 'Total cost of the recommended restock quantity',
  },
  ASC: {
    item: {
      sku: 'The name used to identify products in the inventory',
      name: 'The name used to identify products in the inventory',
      description: 'Textual, referential description of your Item',
      vendorName: 'Common business name of your supplier',
      vendorPrice: 'Cost per unit of a given item',
      moq: 'Minimum quantity your supplier requires you to purchase at a time',
      leadTime: 'Days from ordering to when the item is sellable',
      orderInterval: 'How often you place purchase orders, measured in days',
      serviceLevel: 'Select safety stock level ranging from 0-10 with 0 being no safety stock and 10 being the most safety stock',
      onHand: 'Available FBA inventory synced from AMZ',
      onHandMin: 'Min inventory to keep in stock at an Amazon facility',
      onHandThirdParty: 'The units of the item are stored in your warehouse',
      onHandThirdPartyMin: 'Min inventory to keep in stock outside an Amazon facility',
      isHidden: 'Hide and exclude selected items from sales history and forecast calculations',
      useHistoryOverride: 'Toggles history overrides in order to improve accuracy',
      lotMultipleQty: 'Number of items must be ordered together from the supplier',
      lotMultipleItemName: 'You purchase an item as a different SKU than you sell it as',
      tags: 'Shows the tag applied to this item',
      useBackfill: 'Toggles back-filled data in order to simulate seasonality',
      asin: 'Amazon Standard identification number',
      fnsku: 'Amazon fulfillment identifier used for FBA inventory',
      onHandFbm: 'Available FBM inventory synced from AMZ',
      awdInventoryQty: 'The total available inventory stored in AWD',
      inboundAwd: 'The number of units currently in transit to AWD synced from AMZ',
      doNotOrder: 'Excludes this item from purchase recommendations',
      doNotRestock: 'Excludes this item from restock recommendations',
      isFbm: 'Indicates whether the item is merchant fulfilled',
      inventorySourcePreference: 'Inventory source used for forecasting calculations',
      packageWeight: 'Weight of the item used for calculating shipping costs',
      skuNotes: 'Internal notes related to the SKU',
      prepGuide: 'Amazon-required preparation instructions for the item',
      prepNotes: 'Internal notes related to item preparation',
      supplierRebate: 'Per-unit supplier discount used in margin calculations',
      inboundShippingCost: 'Cost to ship this item from the supplier to AMZ',
      reshippingCost: 'Cost to ship this item from your warehouse to AMZ',
      repackagingMaterialCost: 'Cost of materials used to repackage this item',
      repackingLaborCost: 'Cost of labor used to repackage item',
      dimensionalWeight: 'Dimensional weight calculated by AMZ',
      hazmat: 'Indicates whether the item requires special handling',
      oversized: 'Indicates whether the item is classified as oversized by Amazon',
      category: 'The category this item is classified as',
      upc: 'Universal Product Code for this item',
      ean: 'European Article Number for this item',
      rank: 'Amazon sales rank within its product category',
      average7DayPrice: 'Average Amazon selling price over the last 7 days',
      itemHistoryLength: 'Number of historical days used to calculate average sales for the items',
      boxLength: 'Length of the box used to ship this item to Amazon',
      boxWidth: 'Width of the box used to ship this item to Amazon',
      boxHeight: 'Height of the box used to ship this item to Amazon',
      boxWeight: 'Weight of the box used to ship this item to Amazon',
      isCustom: 'Indicates whether the item is a custom product',
      createdAt: 'The date the item was created',
      s7d: 'Historic sales over period of time',
      s30d: 'Historic sales over period of time',
      s90d: 'Historic sales over period of time',
      s180d: 'Historic sales over period of time',
      s365d: 'Historic sales over period of time',
      lostSale2d: 'Estimated number of units that could have been sold during stockout periods',
      lostSale7d: 'Estimated number of units that could have been sold during stockout periods',
      lostSale14d: 'Estimated number of units that could have been sold during stockout periods',
      lostSale30d: 'Estimated number of units that could have been sold during stockout periods',
      lostSale60d: 'Estimated number of units that could have been sold during stockout periods',
      lostSale90d: 'Estimated number of units that could have been sold during stockout periods',
      lostSale180d: 'Estimated number of units that could have been sold during stockout periods',
      lostSale365d: 'Estimated number of units that could have been sold during stockout periods',
      totalSurcharge: 'Estimated storage surcharge for inventory over 180 days',
      history: '4 years historical sales data for this item',
      imageUrl: 'Item image',
      links: 'Quick links to view this product on Amazon',
      // Additional item-grid fields
      localQty: 'Inventory you have outside of Amazon FBA and FBM',
      inbound: 'The number of units currently in transit synced from AMZ',
      inboundPrice: 'Your current selling price on Amazon',
      inboundSalesLast30Days: 'Amazon Sales Last 30 Days',
      inboundAvailable: 'Inbound Available',
      inboundFcTransfer: 'Total units currently being transferred between fulfillment centers',
      inboundFcProcessing: 'Units currently in Amazon reserved status for transfer or additional processing at a fulfillment center',
      inboundCustomerOrder: 'Units reserved for confirmed customer orders and are temporarily unavailable for other purchases',
      inboundUnfulfillable: 'Units synced from Amazon that are marked as unfulfillable',
      inboundAlert: 'Amazon Inventory Alert - Status: Out Of Stock, Low Stock',
      inboundWorking: 'Inbound inventory with working status',
      mwsFulfillmentFee: 'Fulfillment Fee',
      caseQty: 'Units in a single case pack',
      receiveDate: 'Update to use Expedite Recommendation',
      outOfStockDate: 'Estimated date when inventory will run out',
      openPurchaseOrders: 'Number of days remaining until the next scheduled order date',
      purchaseQty: 'Quantity recommended to purchase for this item',
      safetyStockLtd: 'Suggested buffer inventory to reduce stockout risk',
      openSalesOrders: 'Total quantity committed to all Sales Orders of an item',
      snapshotQty: 'Net item demand after subtracting purchase order and available inventory quantities from projected demand',
      suggestPrice: 'Recommends an optimal listing price for the SKU on Amazon based on current inventory health',
    },
    itemsInPo: {
      details: 'Quick links to view this product on Amazon',
      removeAll: 'Click to remove all suggested items out of purchase order',
      vendorPrice: 'Cost per unit of a given item',
      inboundWorking:'Units in inbound shipments currently being prepared or processed',
      inboundFcProcessing: 'Units currently in Amazon reserved status for transfer or additional processing at a fulfillment center',
      inboundCustomerOrder: 'Units reserved for confirmed customer orders and temporarily unavailable for other purchases',
      lastOrderDate: 'Most recent purchase order date for the item',
      openPurchaseOrders: 'Total open quantity already on existing purchase orders for this item',
      openSalesOrders: 'Total open quantity currently on sales orders for this item',
      vendorName: 'Common business name of your supplier',
      truePurchaseQty: 'Recommended purchase quantity adjusted for daily sales rate, MOQ, and case pack quantity',
    },
    allAvailableItems: {
      vendorPrice: 'Cost per unit of a given item',
      addAll: 'Click to add all items into PO wizard',
      details: 'Quick links to view this product on Amazon',
      meanLtd: 'Forecasted sales based on Lead time and Order interval',
      truePurchaseQty: 'Recommended purchase quantity adjusted for daily sales rate, MOQ, and case pack quantity',
      lastOrderDate: 'Most recent purchase order date for the item',
      openPurchaseOrders: 'Total open quantity already on existing purchase orders for this item',
      inboundUnfulfillable: 'Units synced from Amazon that are marked as unfulfillable',
      openSalesOrders: 'Total open quantity currently on sales orders for this item',
      inboundWorking:'Units in inbound shipments currently being prepared or processed'
    },
    shipment: {
      refNum: 'Enter PO Num to link PO to shipments so inventory is not duplicated and tracked only on shipment',
      shipmentName: 'The name given to this shipment for easy reference',
      status: 'The current stage of the shipment',
      shipmentSource: 'Where the shipment is sent from',
      destinationFulfillmentCenterId: 'Where the shipment is being delivered to',
      options: 'Actions you can take for this shipment (view, edit, or manage)',
      updatedAt: 'The most recent date when the shipment was changed',
      nextStep: 'What you should do next for this shipment',
      createdAt: 'The date when the shipment was created',
      restockType: 'The type of restocking used for this shipment',
      requestedQty: 'The number of items requested to be shipped',
      totalCost: 'The total cost of this shipment',
      orderNotes: 'Extra notes or comments about this shipment',
      receiveDate: 'Estimated date the order will be received',
      receivedQty: 'The total quantity actually received from the shipment',
    },
    purchaseOrder: {
      refNum: 'The unique number used to identify and track the purchase order',
      shipTo: 'The location where the order will be delivered',
      status: 'Current purchase order status',
      orderQty: 'The total number of items ordered',
      openQty: 'The number of items still not delivered or completed',
      docDate: 'The date when the order was created',
      updatedAt: 'The most recent date when the order information was changed',
      shipmentId: 'Amazon FBA shipments linked to this purchase order',
      totalCost: 'The total value of the current order',
      options: 'Actions you can take for this shipment (view, edit, or manage)',
      createdAt: 'The date the purchase order was created',
    },
    campaign: {
      orders90d: 'The total number of orders attributed to a campaign in the last 90 days',
      sales90d: 'The total sales value generated from your advertising efforts in the last 90 days',
      spend90d: 'The total cost accrued by your ads(clicks and impressions) in the last 90 days',
    },
    supply: {
      itemName: 'The name used to identify products in the inventory',
      asin: 'Amazon Standard identification number',
      fnsku: 'Amazon fulfillment identifier used for FBA inventory',
      refNum: 'The unique number used to identify and track the purchase order',
      vendorName: 'Common business name of your supplier',
      reconciledStatus: 'The current stage of the shipment',
      orderQty: 'The total number of items ordered',
      openQty: 'The number of items still not delivered or completed',
      dueDate: 'The date the items were received into inventory',
      docDate: 'The date when the order was created',
      orderKey: 'Unique Amazon Order ID for tracking and reference',
      updatedAt: 'The most recent date when the order information was changed',
      isHidden: 'Hide and exclude selected purchase orders from sales history and forecast calculations',
      imageUrl: 'Item image',
      shipmentId: 'Show Amazon FBA shipments linked to this order',
      documentTotal: 'The total value of the current order',
      view: 'Available actions for this purchase order',
      itemKey: 'Combined ASIN and SKU used as the item identifier',
    },
    itemPricingRecommendation: {
      itemKey: 'Amazon ASIN and seller SKU for the item',
      sku: 'The name used to identify products in the inventory',
      status: 'Shows whether this item is currently synced with Amazon',
      asin: 'Amazon Standard identification number',
      fnsku: 'Amazon fulfillment identifier used for FBA inventory',
      description: 'Textual, referential description of your item',
      recommendedQty: 'Recommended order qty based on forecast models only',
      onHand: 'Available FBA inventory synced from AMZ',
      newBuyBox: 'The most recent buy box price for this product in new condition',
      state: 'Indicates the inventory status based on stock levels and sales velocity',
      minPrice: 'The lowest price you allow the product to be sold at',
      maxPrice: 'The highest price you allow the product to be sold at',
      listPrice: 'Your selling price on Amazon',
      salePrice: 'Promotional price applied to this item',
      inboundPrice: 'Your current selling price on Amazon',
      businessPrice: 'Special price for Amazon Business customers',
      suggestPrice: 'Suggested price calculated from your pricing limits and inventory data',
      suggestedMin: 'Suggested minimum price based on pricing limits and inventory data',
      suggestedMax: 'Suggested maximum price based on pricing limits and inventory data',
      s2d: 'Historic sales over period of time',
      s7d: 'Historic sales over period of time',
      s14d: 'Historic sales over period of time',
      s30d: 'Historic sales over period of time',
      s60d: 'Historic sales over period of time',
      s90d: 'Historic sales over period of time',
      s180d: 'Historic sales over period of time',
      s365d: 'Historic sales over period of time',
      sMtd: 'Historic sales over period of time',
      sYtd: 'Historic sales over period of time',
      logCount: 'Record of recent changes made to this product',
      imageUrl: 'Item image',
      links: 'Quick links to view this product on Amazon',
      trueRecommendedQty: 'Recommended order qty based on recent sales and forecast',
    },
    vendor: {
      name: 'Common business name of your supplier',
      description: 'Textual, referential description of your supplier',
      averageHistoryLength: 'Average monthly sales calculated across all items from this supplier',
      isHidden: 'Hides from your forecasting and purchase recommendations',
      email: 'Supplier email address',
      moq: 'Minimum quantity your supplier requires you to purchase at a time',
      leadTime: 'Days from ordering to when the item is sellable',
      orderInterval: 'How often you place purchase orders, measured in days',
      serviceLevel: 'Select safety stock level ranging from 0-10 with 0 being no safety stock and 10 being the most safety stock',
      targetOrderValue: 'Target purchase order value ({currency}) for this supplier',
      freeFreightMinimum: 'Order value needed for free shipping',
      links: 'Purchasing Daily Sales Rate Settings',
      restockModel: 'Select how inventory is sent to Amazon FBA for this supplier',
    },
    demand: {
      itemName: 'The name used to identify products in the inventory',
      asin: 'Amazon Standard identification number',
      orderKey: 'Unique Amazon Order ID for tracking and reference',
      dueDate: 'Date the item was sold',
      orderQty: 'Total units on a sales order or invoice',
      openQty: 'Outstanding units on a sales order or invoice',
      refNum: 'Order reference number',
      isHidden: 'Hide and exclude selected demands from sales history and forecast calculations',
      itemKey: 'Combined ASIN and SKU used as the item identifier',
      imageUrl: 'Item image',
      fnsku: 'Amazon fulfillment identifier used for FBA inventory',
    },
    supplierList: {
      vendorName: 'Common business name of your supplier',
      orangeAlerts: 'Count of items that need immediate restocking',
      greenAlerts: 'Count of items with healthy stock levels',
      yellowAlerts: 'Count of items that should be restocked soon',
      redAlerts: 'Count of items with no stock available',
      tealAlerts: 'Count of new items with no sales history yet',
      restockUnits: 'Total units recommended for restocking across suppliers',
      restockTotal: 'Total cost of the recommended restock quantity',
      targetOrderValue: 'Target purchase order value ({currency}) for this supplier',
      freeFreightMinimum: 'Order value needed for free shipping',
    },
    // My Suggested POs
    suggestedPos: {
      itemName: 'The name used to identify this item, typically the SKU',
      asin: 'Amazon Standard identification number',
      fnsku: 'Amazon fulfillment identifier used for FBA inventory',
      imageUrl: 'Item image',
      inboundAlert: 'Amazon Inventory Alert - Status: Out Of Stock, Low Stock',
      inboundPrice: 'The unit cost of the item as recorded in the purchase order',
      inboundSalesLast30Days: 'Total inbound quantity received over the past 30 days',
      lotMultipleQty: 'Multiple an item must be ordered by from your supplier',
      truePurchaseQty: 'Recommended purchase quantity adjusted for daily sales rate, MOQ, and case pack quantity',
      snapshotQty: 'The Snapshot quantity of an item',
      doNotOrder: 'Excludes this item from purchase recommendations',
      openPurchaseOrders: 'Total quantity already ordered but not yet received for this item',
      inboundCustomerOrder: 'Number of customer orders fulfilled by Amazon Fulfillment Centers (FBA)',
      inboundFcProcessing: 'Inventory being received and processed at Amazon Fulfillment Centers',
      inboundFcTransfer: 'Total units currently being transferred between fulfillment centers',
      purchaseQty: 'Quantity recommended to purchase for this item',
      mwsFulfillmentFee: 'Fee charged by Amazon to pick, pack, and ship the item',
      inbound: 'The number of units currently in transit synced from AMZ',
      inboundAwd: 'The number of units currently in transit to AWD synced from AMZ',
      inboundWorking: 'Units in inbound shipments currently being prepared or processed ',
      inventorySourcePreference: 'Inventory source used for forecasting calculations',
      isFbm: 'Indicates whether the item is merchant fulfilled',
      lastOrderDate: 'Most recent date the item was ordered',
      details: 'Quick links to view this product on Amazon',
      moq: 'Minimum quantity your supplier requires you to purchase at a time',
      sMtd: 'Historic sales over period of time',
      onHand: 'Available FBA inventory synced from AMZ',
      onHandFbm: 'Available FBM inventory synced from AMZ',
      onHandMin: 'Min inventory to keep in stock at an Amazon facility',
      openSalesOrders: 'Total quantity committed to all Sales Orders of an item',
      outOfStockDate: 'Estimated date when inventory will run out',
      safetyStockLtd: 'Suggested buffer inventory to reduce stockout risk',
      vendorName: 'Common business name of your supplier',
      vendorPrice: 'Adjustable cost per unit of a given item',
      tags: 'Shows the tag applied to this item',
      inboundUnfulfillable: 'Units synced from Amazon that are marked as unfulfillable',
      onHandThirdPartyMin: 'Minimum stock level for the warehouse',
      sYtd: 'Historic sales over period of time',
      lostSale2d: 'Estimated number of units that could have been sold during stockout periods',
      lostSale7d: 'Estimated number of units that could have been sold during stockout periods',
      s7d: 'Historic sales over period of time',
      lostSale14d: 'Estimated number of units that could have been sold during stockout periods',
      lostSale30d: 'Estimated number of units that could have been sold during stockout periods',
      s30d: 'Historic sales over period of time',
      lostSale60d: 'Estimated number of units that could have been sold during stockout periods',
      lostSale90d: 'Estimated number of units that could have been sold during stockout periods',
      s90d: 'Historic sales over period of time',
      lostSale180d: 'Estimated number of units that could have been sold during stockout periods',
      s180d: 'Historic sales over period of time',
      lostSale365d: 'Estimated number of units that could have been sold during stockout periods',
      s365d: 'Historic sales over period of time',
    },
  },
  QBFS: {
    onHandThirdParty: 'Inventory you have outside of a place of sale',
    lotNumber: 'The lot or batch number used to track inventory for this item'
  },
  QBO: {
    onHandThirdParty: 'Inventory you have outside of a place of sale',
  },
  CSV: {
    links: 'Quick links to view this product',
    onHandThirdParty: 'Inventory you have outside of a place of sale'
  },
};

/**
 * Maps column field names to their rich tooltip key, scoped by company type.
 *   - `generic` entries apply to all company types.
 *   - Company-specific entries (e.g. `ASC`) take precedence over `generic`.
 *
 * To add a new rich tooltip:
 *   1. Add the field → key entry in the appropriate bucket below.
 *   2. Add the header/formula config to RICH_HEADER_TOOLTIP_CONFIGS.
 *   3. Add a *ngSwitchCase block in #richHeaderTooltipTpl (HTML) if example
 *      values are needed.
 */
const richTooltipFieldMap: Record<string, Record<string, string>> = {
  generic: {
    daysLeft: 'daysLeft',
    recommendedOrderDate: 'recommendedOrderDate',
    total: 'total',
    nextScheduledOrderDate: 'nextScheduledOrderDate',
    daysUntilNextScheduledOrder: 'daysUntilNextScheduledOrder',
    daysRemaining: 'daysRemaining',
  },
  ASC: {
    [`dueDate${GridName.ItemsInPo}`]: 'dueDate',
    [`dueDate${GridName.SuggestedPos}`]: 'dueDate',
    [`dueDate${GridName.AllAvailableItems}`]: 'dueDate',
    [`onHandThirdParty${GridName.Supply}`]: 'warehouseQtySupply',
    [`onHandThirdParty${GridName.SuggestedPos}`]: 'warehouseQtySupply',
    [`orangeAlerts${GridName.SupplierList}`]: 'restockNow',
    [`greenAlerts${GridName.SupplierList}`]: 'wellStocked',
    [`yellowAlerts${GridName.SupplierList}`]: 'restockSoon',
    [`redAlerts${GridName.SupplierList}`]: 'outOfStock',
    [`tealAlerts${GridName.SupplierList}`]: 'newItem',
    [`restockUnits${GridName.SupplierList}`]: 'restockUnits',
    [`restockTotal${GridName.SupplierList}`]: 'restockTotal',
    [`receiveDate${GridName.Shipment}`]: 'dueDate',
  },
  ASCO: {},
  QBFS: {},
  QBO: {},
  SFY: {},
  CSV: {},
  SHIP_STATION: {},
};

/**
 * Returns the rich tooltip key for the given field and company type,
 * checking the company-specific bucket first, then falling back to generic.
 * Pass gridName to match supply/grid-specific entries (e.g. field + GridName.Supply).
 */
export function getRichTooltipKey(field: string, companyType: string, gridName: string = ''): string {
  return richTooltipFieldMap[companyType]?.[field + gridName]
    || richTooltipFieldMap[companyType]?.[field]
    || richTooltipFieldMap.generic?.[field + gridName]
    || richTooltipFieldMap.generic?.[field]
    || '';
}

export const RICH_HEADER_TOOLTIP_CONFIGS: Record<string, RichTooltipConfig> = {
  warehouseQtySupply: {
    header: 'Warehouse Qty',
    formula: 'Warehouse Qty = Warehouse Qty + AWD Inventory',
  },
  dueDate: {
    header: 'Estimated date the order will be received',
    formula: 'Receive Date = Today + Lead time',
  },
  daysLeft: {
    header: 'Estimated number of days inventory will last',
    formula: 'Projected Days Left in Stock = Projected OOS Date \u2013 Today',
  },
  recommendedOrderDate: {
    header: 'Latest date to place an order to avoid stockout',
    formula: 'Recommended Order Date = Projected OOS Date – Lead Time',
  },
  total: {
    header: 'Total cost commitment for all units',
    formula: 'Total {currency} = Supplier Price ({currency}) × Forecast Recommended Qty',
  },
  meanLtd: {
    header: 'Estimated future sales during the planning period',
    formula: 'Projected Sales = Lead Time + Order Interval',
  },
  nextScheduledOrderDate: {
    header: 'Scheduled date when the next order should be created for this item',
    formula: 'Next Scheduled Order Date = Last Order Date + Order Interval',
  },
  daysUntilNextScheduledOrder: {
    header: 'Number of days remaining until the next scheduled order date ',
    formula: 'Days Until Next Scheduled Order = Next Scheduled Order Date – Today',
  },
  daysRemaining: {
    header: 'Days left to place your order to stay in stock',
    formula: 'Days Left to Order = Recommended Order Date – Today',
  },
  restockNow: {
    header: 'Stock is low and needs restocking soon',
    formula: 'Remaining Days at Amazon < Minimum Days (Restock setting)',
  },
  wellStocked: {
    header: 'Stock level is healthy and does not need restocking',
    formula: 'Remaining Days > Max days or = Infinite',
  },
  restockSoon: {
    header: 'Stock is running low and should be restocked soon',
    formula: 'Minimum Days ≤ Remaining Days at Amazon ≤ Maximum Days (Restock setting)',
  },
  outOfStock: {
    header: 'No stock available and needs urgent restocking',
    formula: 'onHand FBA, onHandFbm, inboundFcTransfer, inboundAvailable, inboundReceiving, and inboundWorking = 0',
  },
  newItem: {
    header: 'New item with no sales history yet',
    formula: 'Items have history length < 1 and Daily sale rate = 0',
  },
  restockUnits: {
    header: 'Total units recommended for restocking across suppliers',
    formula: 'Restock Units = Sum of sugg reorder (sugg reorder > 0)',
  },
  restockTotal: {
    header: 'Total cost of the recommended restock quantity',
    formula: 'Restock Total {currency} = Supplier price ({currency}) × sugg reorder (sugg reorder > 0)',
  },  estimatedMargin: {
    header: 'How much you earn per unit after all fees are deducted',
    formula: 'EM {currency} = Price − (Cost + Amazon Fees)',
    example: '{currency}8 = 25 − (12 + 5)',
    exampleData: { result: '{currency}8', operand1: '25', operator: '−', operand2: '(12 + 5)' },
  },
  estimatedMarginPercentage: {
    header: 'How much profit you make for every {currency}100 in sales',
    formula: 'EM% = (EM ÷ Price) × 100',
    example: '32% = (8 ÷ 25) × 100',
    exampleData: { result: '32%', operand1: '(8 ÷ 25)', operator: '×', operand2: '100' },
  },
  estimatedMarkupPercentage: {
    header: 'How much higher your selling price is compared to your cost',
    formula: 'Markup% = (EM ÷ Cost) × 100',
    example: '66.7% = (8 ÷ 12) × 100',
    exampleData: { result: '66.7%', operand1: '(8 ÷ 12)', operator: '×', operand2: '100' },
  },
  outOfStockPercentage: {
    header: 'The percentage of time your product was not available for sale',
    formula: 'OOS% = Out of stock days ÷ Target days',
    example: '16.7% = 5 ÷ 30',
    exampleData: { result: '16.7%', operand1: '5', operator: '÷', operand2: '30' },
  },
  suggShip: {
    header: 'Keep enough stock at Amazon to meet expected demand',
    formula: 'Sugg Ship = Recommended Restock Quantity',
  },
  suggReorder: {
    header: 'Maintain healthy stock levels in your warehouse',
    formula: 'Sugg Reorder = Recommended Purchase Order Quantity',
  },
  restockNeeded: {
    header: 'How many more units you should send to FBA',
    formula: 'Restock Needed = Ideal Max Qty on Hand (Ideal FBA Inventory) − On Hand FBA Qty',
  },};

/**
 * Maps GridName values to their corresponding section key inside a sectioned
 * company-type tooltip map (e.g. tooltips.ASC).
 */
const GRID_NAME_TO_SECTION: Partial<Record<string, string>> = {
  [GridName.Item]: 'item',
  [GridName.Supply]: 'supply',
  [GridName.Demand]: 'demand',
  [GridName.Shipment]: 'shipment',
  [GridName.PurchaseOrder]: 'purchaseOrder',
  [GridName.ItemsInPo]: 'itemsInPo',
  [GridName.AllAvailableItems]: 'allAvailableItems',
  [GridName.Campaigns]: 'campaign',
  [GridName.ItemPricingRecommendation]: 'itemPricingRecommendation',
  [GridName.RestockSuggestion]: 'restockSuggestion',
  [GridName.Vendor]: 'vendor',
  [GridName.Supplier]: 'vendor',
  [GridName.SuggestedPos]: 'suggestedPos',
  [GridName.SupplierList]: 'supplierList',
};

export function getTip(field: string, companyType: string, gridName: GridName | '' = ''): string {
  const companyTips = tooltips[companyType] as Record<string, any> | undefined;
  const sectionKey = GRID_NAME_TO_SECTION[gridName] ?? '';

  if (companyTips) {
    // 1. Specific section (e.g. ASC → item, supply, vendor, …)
    const sectionTip = companyTips[sectionKey]?.[field];
    if (sectionTip) return sectionTip;

    // 2. Item-section fallback for non-item grids sharing item fields (e.g. Demand, ItemsInPo)
    if (sectionKey && sectionKey !== 'item') {
      const itemTip = companyTips['item']?.[field];
      if (itemTip) return itemTip;
    }

    // 3. No gridName: scan all sections (e.g. called without a gridName)
    if (!sectionKey) {
      for (const val of Object.values(companyTips)) {
        const tip = val?.[field];
        if (tip) return tip;
      }
    }

    // 4. Legacy flat lookup (QBFS, QBO, CSV use a flat structure)
    const flatTip = companyTips[field + gridName] || companyTips[field];
    if (flatTip) return flatTip;
  }

  // 5. Generic fallback
  return tooltips.generic[field + gridName] || tooltips.generic[field] || '';
}

/**
 * Replaces `{currency}` placeholders in a tooltip string with the given currency symbol.
 * Defaults to '$' when no symbol is provided.
 */
export function applyCurrencySymbol(text: string, symbol: string = '$'): string {
  return text.replace(/\{currency\}/g, symbol);
}

export enum CountryCode {
  US = 'US'
}
